import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight, Calendar, Tag, Building2 } from 'lucide-react';
import { newsletters } from '../newsletterData';
import SEO from '../components/SEO';

const Newsletters = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedNewsletter, setExpandedNewsletter] = useState(null);
  
  const ITEMS_PER_PAGE = 4;
  
  // Get unique industries and categories
  const industries = ['All', ...new Set(newsletters.map(n => n.industry))];
  const categories = ['All', ...new Set(newsletters.map(n => n.category))];
  
  // Filter newsletters
  const filteredNewsletters = newsletters.filter(newsletter => {
    const matchesIndustry = selectedIndustry === 'All' || newsletter.industry === selectedIndustry;
    const matchesCategory = selectedCategory === 'All' || newsletter.category === selectedCategory;
    return matchesIndustry && matchesCategory;
  });
  
  const totalPages = Math.ceil(filteredNewsletters.length / ITEMS_PER_PAGE);
  const currentNewsletters = filteredNewsletters.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );
  
  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };
  
  const handleNext = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };
  
  const handleFilterChange = () => {
    setCurrentPage(0); // Reset to first page when filter changes
  };

  return (
    <div>
      <SEO 
        title="Newsletters"
        description="Stay informed with Munoret's industry newsletters covering workforce trends, employee achievements, economic analysis, and innovative staffing solutions across all sectors we serve."
        keywords="recruitment newsletters, workforce trends, staffing news, industry insights, hiring updates, employment news, HR newsletters"
      />
      
      {/* Hero Section */}
      <section style={{ 
        background: 'linear-gradient(180deg, var(--bg-light) 0%, var(--bg-primary) 100%)',
        padding: '80px 0 60px',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <motion.h1 
            className="display-hero"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginBottom: '24px' }}
          >
            Industry Newsletters
          </motion.h1>
          <motion.p 
            className="body-large"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}
          >
            In-depth analysis, employee achievements, economic insights, and innovative staffing solutions
          </motion.p>
        </div>
      </section>

      {/* Filters Section */}
      <section style={{ padding: '40px 0', background: 'var(--bg-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div style={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Building2 size={20} style={{ color: 'var(--text-muted)' }} />
              <select
                value={selectedIndustry}
                onChange={(e) => {
                  setSelectedIndustry(e.target.value);
                  handleFilterChange();
                }}
                className="form-input"
                style={{ width: 'auto', minWidth: '250px' }}
              >
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Tag size={20} style={{ color: 'var(--text-muted)' }} />
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  handleFilterChange();
                }}
                className="form-input"
                style={{ width: 'auto', minWidth: '200px' }}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            
            <div style={{ 
              color: 'var(--text-secondary)',
              fontSize: '14px',
              padding: '0 16px'
            }}>
              Showing {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, filteredNewsletters.length)} of {filteredNewsletters.length} newsletters
            </div>
          </div>
        </div>
      </section>

      {/* Newsletters Grid */}
      <section style={{ padding: '60px 0', minHeight: '800px' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentPage}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '32px',
                marginBottom: '48px'
              }}
            >
              {currentNewsletters.map((newsletter, index) => (
                <motion.article
                  key={newsletter.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    y: -8,
                    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
                    transition: { duration: 0.2 }
                  }}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid var(--border-medium)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  {/* Newsletter Header */}
                  <div style={{
                    background: 'var(--bg-light)',
                    padding: '20px',
                    borderBottom: '1px solid var(--border-medium)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        background: 'var(--brand-primary)',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {newsletter.category}
                      </span>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: 'var(--text-muted)',
                        fontSize: '13px'
                      }}>
                        <Calendar size={14} />
                        {newsletter.date}
                      </div>
                    </div>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      lineHeight: '1.4',
                      marginBottom: '8px'
                    }}>
                      {newsletter.title}
                    </h3>
                    <div style={{
                      fontSize: '13px',
                      color: 'var(--text-secondary)',
                      fontWeight: '500'
                    }}>
                      {newsletter.industry}
                    </div>
                  </div>
                  
                  {/* Newsletter Content */}
                  <div style={{
                    padding: '20px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      marginBottom: '16px',
                      flex: 1
                    }}>
                      {newsletter.excerpt}
                    </p>
                    <button className="btn-secondary" style={{
                      width: '100%',
                      fontSize: '14px',
                      padding: '10px 16px'
                    }}
                    onClick={() => setExpandedNewsletter(newsletter)}
                    >
                      Read Full Article
                    </button>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          </AnimatePresence>
          
          {/* Pagination Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
            marginTop: '48px'
          }}>
            <button
              onClick={handlePrevious}
              disabled={currentPage === 0}
              className="btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: currentPage === 0 ? 0.5 : 1,
                cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <ChevronLeft size={20} />
              Previous
            </button>
            
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setCurrentPage(i);
                    window.scrollTo({ top: 400, behavior: 'smooth' });
                  }}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    border: i === currentPage ? '2px solid var(--brand-primary)' : '1px solid var(--border-medium)',
                    background: i === currentPage ? 'var(--brand-primary)' : 'white',
                    color: i === currentPage ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    fontWeight: i === currentPage ? '600' : '400',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (i !== currentPage) {
                      e.target.style.borderColor = 'var(--brand-primary)';
                      e.target.style.color = 'var(--brand-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (i !== currentPage) {
                      e.target.style.borderColor = 'var(--border-medium)';
                      e.target.style.color = 'var(--text-primary)';
                    }
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages - 1}
              className="btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Next
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Detail Modal */}
      {expandedNewsletter && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          overflowY: 'auto'
        }}
        onClick={() => setExpandedNewsletter(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              background: 'white',
              borderRadius: '24px',
              padding: '48px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setExpandedNewsletter(null)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                fontSize: '32px',
                cursor: 'pointer',
                color: 'var(--text-secondary)'
              }}
            >
              ×
            </button>

            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  background: 'var(--brand-primary)',
                  color: 'white',
                  padding: '6px 16px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {expandedNewsletter.category}
                </span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px'
                }}>
                  <Calendar size={16} />
                  {expandedNewsletter.date}
                </div>
              </div>

              <h2 className="heading-large" style={{ marginBottom: '12px' }}>
                {expandedNewsletter.title}
              </h2>

              <div style={{
                fontSize: '15px',
                color: 'var(--text-secondary)',
                fontWeight: '500',
                marginBottom: '24px',
                paddingBottom: '24px',
                borderBottom: '1px solid var(--border-medium)'
              }}>
                <Building2 size={16} style={{ display: 'inline', marginRight: '8px' }} />
                {expandedNewsletter.industry}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <p style={{
                color: 'var(--text-primary)',
                fontSize: '16px',
                lineHeight: '1.8',
                marginBottom: '20px'
              }}>
                {expandedNewsletter.excerpt}
              </p>
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '16px',
                lineHeight: '1.8'
              }}>
                {expandedNewsletter.content}
              </p>
            </div>

            <button 
              className="btn-primary" 
              style={{ width: '100%' }}
              onClick={() => setExpandedNewsletter(null)}
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Newsletters;
